"use client";

import { Form } from "react-aria-components";
import { InputSelect, useSelect } from "@jetblue/kit/input-select";
import { InputCheckbox } from "@jetblue/kit/input-checkbox";

import type { useFlightSearchForm } from "./hooks/use-flight-search-form";
import { MultiCity } from "./multi-city";
import { OneWay } from "./one-way";
import { PassengersInput } from "./components/passengers-input";
import { RoundTrip } from "./round-trip";

export type StatelessFlightSearchFormProperties = ReturnType<
  typeof useFlightSearchForm
>;
export const StatelessFlightSearchForm = ({
  bookingUrl = "https://www.jetblue.com/booking/flights",
  errors,
  formList,
  labels,
  odQuery,
  onTripTypeChange,
  passengers,
  showDatePickerPricing,
  showPromoCode,
  formReference,
  handleSubmit,
  tripType,
}: StatelessFlightSearchFormProperties) => (
  <Form
    action={bookingUrl}
    className="p-4"
    data-fs-element="booker"
    onSubmit={handleSubmit}
    ref={formReference}
    validationBehavior="aria"
  >
    <div className="mb-2 flex items-center justify-between">
      <div className="flex flex-row items-center gap-6">
        <InputSelect
          aria-label="Trip Type"
          className="text-s-body-extra-bold [&_button]:cursor-pointer"
          data-fs-element="trip-type"
          label="Trip Type"
          options={[
            { label: labels.tripType.roundTrip, value: "round-trip" },
            { label: labels.tripType.oneWay, value: "one-way" },
            { label: labels.tripType.multiCity, value: "multi-city" },
          ]}
          popoverProperties={{
            containerPadding: 0,
            placement: "bottom start",
            shouldFlip: false,
          }}
          {...useSelect({
            initialSelectedKey: tripType,
            onSelectionChange: onTripTypeChange,
          })}
          value={tripType}
          variant="simple"
        />
        <PassengersInput labels={labels.passengersDropdown} {...passengers} />
      </div>

      {tripType !== "multi-city" && (
        <InputCheckbox
          className="hidden md:block"
          data-fs-element="points-toggle"
          isSelected={formList.usePoints}
          onChange={() => {
            formList.setUsePoints((current) => !current);
          }}
        >
          {labels.loyaltyCheckbox}
        </InputCheckbox>
      )}
    </div>

    {tripType === "one-way" && (
      <OneWay
        errors={errors}
        formList={formList}
        labels={labels}
        odQuery={odQuery}
        passengers={{
          adults: passengers.controlAdults.value,
          children: passengers.controlChildren.value,
          infants: passengers.controlInfants.value,
        }}
        showDatePickerPricing={Boolean(showDatePickerPricing)}
        showPromoCode={showPromoCode}
      />
    )}

    {tripType === "round-trip" && (
      <RoundTrip
        errors={errors}
        formList={formList}
        labels={labels}
        odQuery={odQuery}
        passengers={{
          adults: passengers.controlAdults.value,
          children: passengers.controlChildren.value,
          infants: passengers.controlInfants.value,
        }}
        showDatePickerPricing={Boolean(showDatePickerPricing)}
        showPromoCode={showPromoCode}
      />
    )}

    {tripType === "multi-city" && (
      <MultiCity
        errors={errors}
        formList={formList}
        labels={labels}
        odQuery={odQuery}
        passengers={{
          adults: passengers.controlAdults.value,
          children: passengers.controlChildren.value,
          infants: passengers.controlInfants.value,
        }}
      />
    )}

    <div className="hidden">
      <input
        name="isMultiCity"
        readOnly
        value={tripType === "multi-city" ? "true" : "false"}
      />
      <input
        name="noOfRoute"
        readOnly
        value={
          tripType === "multi-city" ? formList.routes.length.toString() : "1"
        }
      />
      <input name="roundTripFaresFlag" readOnly value="false" />
      <input name="sharedMarket" readOnly value="false" />
      <input name="usePoints" readOnly value={formList.usePoints.toString()} />
    </div>
  </Form>
);
